import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Crown, Save, ShieldOff, RotateCcw } from "lucide-react";

/**
 * Admin console tab for the House system — Update 5's minimum viable slice.
 * Admins can rename any of the eight seeded Houses and re-tune its crest,
 * banner URL, color, motto and lore. Contribution moderation + guild
 * moderation land in a follow-up pass; this covers the "you can edit them
 * later" promise made when the Houses were auto-seeded.
 */
export default function AdminHouses() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-houses"],
    queryFn: async () => (await api.get("/houses")).data,
    staleTime: 30_000,
  });
  const houses = data?.houses || [];

  return (
    <div className="max-w-4xl" data-testid="admin-houses-page">
      <div className="flex items-center gap-2 mb-4">
        <Crown className="h-4 w-4 text-amber-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300">Houses · Admin</span>
      </div>
      <h1 className="font-display text-2xl sm:text-3xl text-white mb-4">Edit the eight banners</h1>

      {isLoading ? (
        <div className="text-white/50 text-sm">Loading houses…</div>
      ) : (
        <div className="space-y-3">
          {houses.map((h) => (
            <HouseEditorRow key={h.key} h={h} onSaved={refetch} />
          ))}
        </div>
      )}

      <div className="mt-8">
        <VoidContributionsPanel houses={houses} />
      </div>
    </div>
  );
}

function VoidContributionsPanel({ houses }) {
  const qc = useQueryClient();
  const [houseKey, setHouseKey] = useState("");
  const [cardId, setCardId] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [category, setCategory] = useState("");
  const [includeVoided, setIncludeVoided] = useState(false);
  const [reason, setReason] = useState("");
  const [selected, setSelected] = useState(new Set());

  const { data, refetch, isFetching } = useQuery({
    queryKey: ["admin-house-contribs", houseKey, cardId, ownerId, category, includeVoided],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (houseKey) params.set("house_key", houseKey);
      if (cardId) params.set("card_id", cardId);
      if (ownerId) params.set("owner_id", ownerId);
      if (category) params.set("category", category);
      if (includeVoided) params.set("include_voided", "true");
      params.set("limit", "100");
      return (await api.get(`/admin/houses/contributions?${params.toString()}`)).data;
    },
    staleTime: 5_000,
  });
  const rows = data?.items || [];

  const voidBulk = useMutation({
    mutationFn: async () => (await api.post("/admin/houses/contributions/void", {
      contribution_ids: [...selected],
      reason: reason.trim() || "voided by admin",
    })).data,
    onSuccess: (r) => {
      toast.success(`Voided ${r.voided} contribution row${r.voided === 1 ? "" : "s"}`);
      setSelected(new Set());
      setReason("");
      qc.invalidateQueries({ queryKey: ["admin-house-contribs"] });
      qc.invalidateQueries({ queryKey: ["houses"] });
      refetch();
    },
    onError: (e) => toast.error(apiError(e, "Void failed")),
  });

  const restore = useMutation({
    mutationFn: async (cid) => (await api.post(`/admin/houses/contributions/${cid}/restore`)).data,
    onSuccess: () => {
      toast.success("Restored");
      qc.invalidateQueries({ queryKey: ["admin-house-contribs"] });
      qc.invalidateQueries({ queryKey: ["houses"] });
      refetch();
    },
    onError: (e) => toast.error(apiError(e, "Restore failed")),
  });

  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.filter((r) => !r.voided).map((r) => r.id)));
  };

  return (
    <div className="rounded-2xl border border-red-400/30 bg-red-400/[0.04] p-4" data-testid="admin-void-contribs">
      <div className="flex items-center gap-2 mb-3">
        <ShieldOff className="h-4 w-4 text-red-300" />
        <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-red-300">Void fraudulent House scores</span>
      </div>
      <p className="text-[12px] text-white/60 mb-3">Filter contribution rows and void the ones you want scrubbed from a House&apos;s live leaderboard. Voided rows still exist for audit — you can restore any one of them.</p>

      <div className="grid sm:grid-cols-4 gap-2 mb-3">
        <select value={houseKey} onChange={(e) => setHouseKey(e.target.value)}
          data-testid="admin-void-house-filter"
          className="h-9 rounded-md border border-white/10 bg-white/5 text-white text-sm px-2">
          <option value="">All houses</option>
          {houses.map((h) => <option key={h.key} value={h.key}>{h.name}</option>)}
        </select>
        <Input placeholder="Card ID" value={cardId} onChange={(e) => setCardId(e.target.value)}
          data-testid="admin-void-card-filter" className="bg-white/5 border-white/10 text-white text-xs h-9" />
        <Input placeholder="Owner ID" value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
          data-testid="admin-void-owner-filter" className="bg-white/5 border-white/10 text-white text-xs h-9" />
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="admin-void-category-filter"
          className="h-9 rounded-md border border-white/10 bg-white/5 text-white text-sm px-2">
          <option value="">All categories</option>
          {["arena","dungeon","race","show","quest","craft","gather","trade","upgrade","merge","misc"].map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] text-white/70">
          <input type="checkbox" checked={includeVoided} onChange={(e) => setIncludeVoided(e.target.checked)}
            data-testid="admin-void-include-voided" className="accent-red-400" />
          Show already-voided rows
        </label>
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}
          data-testid="admin-void-refresh" className="border-white/15 text-white/80">
          {isFetching ? "Loading…" : "Refresh"}
        </Button>
      </div>

      <div className="max-h-96 overflow-y-auto rounded-lg border border-white/10 bg-black/40">
        <table className="w-full text-[11.5px]">
          <thead className="sticky top-0 bg-black/70 text-white/50 font-mono text-[10px] uppercase tracking-[0.2em]">
            <tr>
              <th className="p-2 text-left w-8">
                <input type="checkbox" checked={rows.length > 0 && selected.size === rows.filter((r) => !r.voided).length}
                  onChange={toggleAll} data-testid="admin-void-toggle-all" className="accent-red-400" />
              </th>
              <th className="p-2 text-left">Time</th>
              <th className="p-2 text-left">Card / Owner</th>
              <th className="p-2 text-left">House</th>
              <th className="p-2 text-left">Cat</th>
              <th className="p-2 text-right">Pts</th>
              <th className="p-2 text-left">State</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="p-3 text-white/45 text-center">No rows.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-white/5" data-testid={`admin-void-row-${r.id}`}>
                <td className="p-2">
                  {!r.voided && (
                    <input type="checkbox" checked={selected.has(r.id)}
                      onChange={(e) => setSelected((prev) => {
                        const next = new Set(prev);
                        if (e.target.checked) next.add(r.id); else next.delete(r.id);
                        return next;
                      })}
                      data-testid={`admin-void-check-${r.id}`} className="accent-red-400" />
                  )}
                </td>
                <td className="p-2 text-white/55 font-mono text-[10px]">{r.ts?.slice(0, 19)?.replace("T", " ")}</td>
                <td className="p-2 text-white/70 font-mono text-[10px]">
                  <div>{r.card_id?.slice(0, 10) || "—"}</div>
                  <div className="text-white/40">{r.owner_id?.slice(0, 10) || "—"}</div>
                </td>
                <td className="p-2 text-white/70">{r.house_key_at_earn}</td>
                <td className="p-2 text-white/70">{r.category}</td>
                <td className="p-2 text-white/80 font-mono text-right">{r.points}</td>
                <td className="p-2">
                  {r.voided ? (
                    <div className="flex items-center gap-1">
                      <span className="font-mono text-[10px] uppercase text-red-300">voided</span>
                      <Button size="sm" onClick={() => restore.mutate(r.id)} disabled={restore.isPending}
                        data-testid={`admin-void-restore-${r.id}`}
                        className="h-5 px-1 text-[9px] bg-white/10 hover:bg-white/20 text-white">
                        <RotateCcw className="h-2.5 w-2.5" />
                      </Button>
                    </div>
                  ) : (
                    <span className="font-mono text-[10px] uppercase text-emerald-300">live</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center gap-2">
        <Input placeholder="Reason (required to void)" value={reason} onChange={(e) => setReason(e.target.value)}
          data-testid="admin-void-reason" className="bg-white/5 border-white/10 text-white text-xs h-9 flex-1" />
        <Button size="sm" onClick={() => voidBulk.mutate()}
          disabled={voidBulk.isPending || selected.size === 0 || !reason.trim()}
          data-testid="admin-void-submit"
          className="bg-red-400 text-black hover:bg-red-300 h-9">
          <ShieldOff className="h-4 w-4 mr-1" /> Void {selected.size} row{selected.size === 1 ? "" : "s"}
        </Button>
      </div>
    </div>
  );
}

function HouseEditorRow({ h, onSaved }) {
  const qc = useQueryClient();
  const [name, setName] = useState(h.name || "");
  const [motto, setMotto] = useState(h.motto || "");
  const [lore, setLore] = useState(h.lore || "");
  const [color, setColor] = useState(h.color || "#94a3b8");
  const [crest, setCrest] = useState(h.crest || "");
  const [banner, setBanner] = useState(h.banner || "");

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/admin/houses/${h.key}`, {
      name: name.trim() || null,
      motto: motto.trim() || null,
      lore: lore.trim() || null,
      color: color || null,
      crest: crest.trim() || null,
      banner: banner.trim() || null,
    })).data,
    onSuccess: () => {
      toast.success(`Saved ${name}`);
      qc.invalidateQueries({ queryKey: ["admin-houses"] });
      qc.invalidateQueries({ queryKey: ["houses"] });
      onSaved?.();
    },
    onError: (e) => toast.error(apiError(e, "Save failed")),
  });

  return (
    <div
      className="rounded-2xl border p-4"
      style={{ borderColor: `${color}55`, background: `${color}10` }}
      data-testid={`admin-house-row-${h.key}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <div
          className="h-9 w-9 rounded-lg flex items-center justify-center text-xl"
          style={{ background: `${color}22`, border: `1px solid ${color}55` }}
        >
          {crest || "🏛"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/45">{h.key}</div>
          <div className="font-display text-lg text-white truncate">{name}</div>
        </div>
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}
          data-testid={`admin-house-save-${h.key}`}
          className="bg-amber-400 text-black hover:bg-amber-300">
          <Save className="h-4 w-4 mr-1" /> {save.isPending ? "Saving…" : "Save"}
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-2 text-sm">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Name</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} data-testid={`admin-house-name-${h.key}`}
            className="mt-1 bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Crest (emoji or 1–4 chars)</label>
          <Input value={crest} onChange={(e) => setCrest(e.target.value)} maxLength={8}
            data-testid={`admin-house-crest-${h.key}`} className="mt-1 bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Motto</label>
          <Input value={motto} onChange={(e) => setMotto(e.target.value)} data-testid={`admin-house-motto-${h.key}`}
            className="mt-1 bg-white/5 border-white/10 text-white" />
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Color</label>
          <div className="mt-1 flex items-center gap-2">
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
              data-testid={`admin-house-color-${h.key}`}
              className="h-9 w-14 rounded-md border border-white/10 bg-white/5 cursor-pointer" />
            <Input value={color} onChange={(e) => setColor(e.target.value)}
              className="bg-white/5 border-white/10 text-white font-mono text-xs" />
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Lore</label>
          <Textarea value={lore} onChange={(e) => setLore(e.target.value)} maxLength={800}
            data-testid={`admin-house-lore-${h.key}`}
            className="mt-1 bg-white/5 border-white/10 text-white min-h-[60px]" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/55">Banner image URL (optional)</label>
          <Input value={banner} onChange={(e) => setBanner(e.target.value)} placeholder="https://…"
            data-testid={`admin-house-banner-${h.key}`} className="mt-1 bg-white/5 border-white/10 text-white font-mono text-xs" />
        </div>
      </div>
    </div>
  );
}
